import { getCountryData, TCountryCode } from 'countries-list';
import i18nCountries from 'i18n-iso-countries';
import countriesEn from 'i18n-iso-countries/langs/en.json';
import countriesNl from 'i18n-iso-countries/langs/nl.json';
import nationalities from 'i18n-nationality';
import nationalityEn from 'i18n-nationality/langs/en.json';
import nationalityNl from 'i18n-nationality/langs/nl.json';
import iso from 'iso-3166-1';

import { CUSTOM_COUNTRIES, RAW_COUNTRIES } from '@/constants/countries';

import { CountryCode3 } from '@/types/common';
import { Language } from '@/types/i18n';

nationalities.registerLocale({
  ...nationalityEn,
  nationalities: {
    ...nationalityEn.nationalities,
    AI: 'Anguillan',
    AQ: 'Antarctican',
    AS: 'American Samoan',
    AW: 'Aruban',
  },
});
nationalities.registerLocale({
  ...nationalityNl,
  nationalities: {
    ...nationalityNl.nationalities,
    AI: 'Anguillan',
    AQ: 'Antarctisch',
    AS: 'Amerikaans-Samoaans',
    AW: 'Arubaans',
  },
});
i18nCountries.registerLocale({
  ...countriesEn,
  countries: {
    ...countriesEn.countries,
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AS: 'American Samoa',
    AW: 'Aruba',
  },
});
i18nCountries.registerLocale({
  ...countriesNl,
  countries: {
    ...countriesNl.countries,
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AS: 'Amerikaans-Samoa',
    AW: 'Aruba',
  },
});

type CountryTranslator = {
  getCountryName: (code3: string, language: Language) => string | undefined;
  getNationalityName: (code3: string, language: Language) => string | undefined;
};

type TranslateCountry = Record<CountryCode3, string> & {
  [key: string]: string;
};

export type TranslationCountries = {
  country: TranslateCountry;
  policyByCountry: TranslateCountry;
  nationality: TranslateCountry;
};

type TranslationCountriesByLangue = {
  [Language.English]?: TranslationCountries;
  [Language.Dutch]?: TranslationCountries;
};

const TranslatedCountries: TranslationCountriesByLangue = {};

const convertAlpha3ToAplha2 = (code3: string): string | undefined => {
  const country = iso.whereAlpha3(code3);
  return country?.alpha2 || nationalities.alpha3ToAlpha2(code3);
};

export const i18nCountryTranslator: CountryTranslator = {
  getCountryName: (code3: string, language: Language) =>
    i18nCountries.getName(code3, language),
  getNationalityName: (code3: string, language: Language) => {
    const countryCode = convertAlpha3ToAplha2(code3) || code3;
    return nationalities.getName(countryCode, language);
  },
};

export const getCountryTranslations = (
  language: Language,
  translator: CountryTranslator = i18nCountryTranslator,
  code?: CountryCode3,
) => {
  if (code && TranslatedCountries[language]?.country[code]) {
    return TranslatedCountries[language];
  }
  const translations: TranslationCountries = {
    country: {} as TranslateCountry,
    policyByCountry: {} as TranslateCountry,
    nationality: {} as TranslateCountry,
  };

  RAW_COUNTRIES.forEach(country => {
    const code3 = country.code3 as CountryCode3;

    translations.country[code3] =
      translator.getCountryName(code3, language) || country.name;
    translations.nationality[code3] =
      translator.getNationalityName(code3, language) ||
      translator.getNationalityName(code3, Language.English) ||
      country.name;
  });

  CUSTOM_COUNTRIES.forEach(country => {
    if (country.code3) {
      const code3 = country.code3 as CountryCode3;
      translations.country[code3] = country.name;
      translations.nationality[code3] = country.name;
    }
  });

  TranslatedCountries[language] = { ...translations };

  return translations;
};

/**
 * Check if country code is iso 2 or iso 3. If iso 2 and in the list of countries, convert to iso 3.
 * If iso 3, return the iso 3.
 * If not in the list of countries, return the default code.
 * @param code - The country code
 * @returns The country code3
 */
export const getCorrectCountryCode = (code?: string) => {
  if (!code) return '';
  if (code.length === 2) {
    const isoCode3 = getCountryData(code as TCountryCode).iso3;
    return isoCode3 || code;
  }
  return code;
};
